import React from "react";
import { GEOSERVER_CONFIG, DEFAULT_WFS_PARAMS } from "../../../config/api";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Divider,
  Paper,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Map as MapIcon,
  Add as AddIcon,
  Bookmark as BookmarkIcon,
  LocationOn as LocationOnIcon,
  ExpandLess,
  ExpandMore,
  AdsClick as AdsClickIcon,
  Layers as LayersIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import PinDropIcon from "@mui/icons-material/PinDrop";

/** filter tool drawer panel */
export default function FilterToolPanel(ctx) {
  const {
    filterLayer,
    setFilterLayer,
    filterClauses,
    setFilterClauses,
    filterInvertWhere,
    setFilterInvertWhere,
    filterLayerFields,
    setFilterLayerFields,
    layerGroups,
    queryFieldValues,
    queryFieldValuesLoading,
    fetchFieldValues,
    activeFilters,
    setActiveFilters,
    wmsLayersRef,
    layerExtentsRef,
    layerNamesRef,
    applyFilterToLayer,
    setAttributeTableOpen,
    setAttributeLoading,
    setAttributeColumns,
    setAttributeData,
    setAttributeLayerName,
    setSelectedRows,
    attributeFeaturesRef,
    setAttributeTableDocked,
    setTablePage,
    GEOSERVER_CONFIG,
    DEFAULT_WFS_PARAMS,
    onCloseTool,
  } = ctx;

  return (
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
                  >
                    Filter Features
                  </Typography>

                  {/* Select Layer */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5, fontWeight: 500 }}
                    >
                      Select Layer
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={filterLayer}
                      onChange={(e) => {
                        setFilterLayer(e.target.value);
                        // Filter ke liye alag fields fetch karo
                        const layerKey = e.target.value;
                        const wmsLayerName = layerExtentsRef.current[layerKey];
                        if (!wmsLayerName) return;
                        fetch(
                          `${GEOSERVER_CONFIG.wfsUrl}?${new URLSearchParams({
                            ...DEFAULT_WFS_PARAMS,
                            typeName: wmsLayerName,
                            maxFeatures: 1,
                          })}`,
                        )
                          .then((r) => r.json())
                          .then((data) => {
                            if (data.features?.length > 0) {
                              setFilterLayerFields(
                                Object.keys(data.features[0].properties),
                              );
                            }
                          })
                          .catch(console.error);
                      }}
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value="">-- Select Layer --</MenuItem>
                      {Object.entries(layerGroups).map(([groupKey, group]) =>
                        Object.entries(group.children).map(
                          ([layerKey, layer]) => (
                            <MenuItem key={layerKey} value={layerKey}>
                              {layer.name}
                            </MenuItem>
                          ),
                        ),
                      )}
                    </Select>
                  </Box>

                  {/* Expression Builder */}
                  <Box
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      overflow: "visible",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        bgcolor: "#f0f4fa",
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, fontSize: 12 }}
                      >
                        Expression
                      </Typography>
                    </Box>

                    <Box sx={{ p: 1 }}>
                      {filterClauses.map((clause, idx) => (
                        <Box
                          key={clause.id}
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            alignItems: "center",
                            mb: 0.8,
                            flexWrap: "nowrap",
                          }}
                        >
                          {idx === 0 ? (
                            <Typography
                              variant="caption"
                              sx={{
                                width: 46,
                                fontSize: 12,
                                fontWeight: 500,
                                flexShrink: 0,
                              }}
                            >
                              Where
                            </Typography>
                          ) : (
                            <Select
                              size="small"
                              value={clause.conjunction || "And"}
                              onChange={(e) => {
                                const u = [...filterClauses];
                                u[idx].conjunction = e.target.value;
                                setFilterClauses(u);
                              }}
                              sx={{ width: 60, fontSize: 11 }}
                            >
                              <MenuItem value="And" sx={{ fontSize: 11 }}>
                                And
                              </MenuItem>
                              <MenuItem value="Or" sx={{ fontSize: 11 }}>
                                Or
                              </MenuItem>
                            </Select>
                          )}

                          {/* Field */}
                          <Select
                            size="small"
                            value={clause.field}
                            onChange={(e) => {
                              const u = [...filterClauses];
                              u[idx].field = e.target.value;
                              setFilterClauses(u);
                            }}
                            sx={{ flex: 1, fontSize: 11, minWidth: 0 }}
                            displayEmpty
                          >
                            <MenuItem
                              value=""
                              sx={{ fontSize: 11, color: "#aaa" }}
                            >
                              Field
                            </MenuItem>
                            {filterLayerFields.map((f) => (
                              <MenuItem key={f} value={f} sx={{ fontSize: 11 }}>
                                {f}
                              </MenuItem>
                            ))}
                          </Select>

                          {/* Operator */}
                          <Select
                            size="small"
                            value={clause.operator}
                            onChange={(e) => {
                              const u = [...filterClauses];
                              u[idx].operator = e.target.value;
                              setFilterClauses(u);
                            }}
                            sx={{ flex: 1.2, fontSize: 11, minWidth: 0 }}
                          >
                            {[
                              "is equal to",
                              "is not equal to",
                              "is greater than",
                              "is less than",
                              "contains",
                              "starts with",
                              "is blank",
                              "is not blank",
                            ].map((op) => (
                              <MenuItem
                                key={op}
                                value={op}
                                sx={{ fontSize: 11 }}
                              >
                                {op}
                              </MenuItem>
                            ))}
                          </Select>

                          {/* Value */}
                          <Box
                            sx={{ flex: 1, minWidth: 0, position: "relative" }}
                          >
                            <TextField
                              size="small"
                              placeholder="Value"
                              value={clause.value}
                              onChange={(e) => {
                                const u = [...filterClauses];
                                u[idx].value = e.target.value;
                                setFilterClauses(u);
                                if (
                                  e.target.value.length > 0 &&
                                  queryFieldValues[clause.id]
                                ) {
                                  const u2 = [...filterClauses];
                                  u2[idx].showSuggestions = true;
                                  setFilterClauses(u2);
                                }
                              }}
                              onFocus={() => {
                                if (clause.field && filterLayer) {
                                  fetchFieldValues(
                                    filterLayer,
                                    clause.field,
                                    clause.id,
                                  );
                                }
                                const u = [...filterClauses];
                                u[idx].showSuggestions = true;
                                setFilterClauses(u);
                              }}
                              onBlur={() => {
                                setTimeout(() => {
                                  const u = [...filterClauses];
                                  u[idx].showSuggestions = false;
                                  setFilterClauses(u);
                                }, 200);
                              }}
                              InputProps={{
                                endAdornment: queryFieldValuesLoading[
                                  clause.id
                                ] ? (
                                  <CircularProgress size={12} />
                                ) : null,
                              }}
                              sx={{
                                width: "100%",
                                "& input": { fontSize: 11 },
                              }}
                            />
                            {/* Suggestions Dropdown */}
                            {clause.showSuggestions &&
                              queryFieldValues[clause.id]?.length > 0 && (
                                <Paper
                                  sx={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    maxHeight: 150,
                                    overflow: "auto",
                                    zIndex: 99999,
                                    border: "1px solid #1976d2",
                                    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                                    bgcolor: "white",
                                  }}
                                >
                                  {queryFieldValues[clause.id]
                                    .filter(
                                      (v) =>
                                        clause.value === "" ||
                                        v
                                          .toLowerCase()
                                          .includes(clause.value.toLowerCase()),
                                    )
                                    .map((val, vi) => (
                                      <Box
                                        key={vi}
                                        onMouseDown={() => {
                                          const u = [...filterClauses];
                                          u[idx].value = val;
                                          u[idx].showSuggestions = false;
                                          setFilterClauses(u);
                                        }}
                                        sx={{
                                          px: 1.5,
                                          py: 0.6,
                                          fontSize: 11,
                                          cursor: "pointer",
                                          "&:hover": { bgcolor: "#e3f2fd" },
                                          borderBottom: "1px solid #f0f0f0",
                                        }}
                                      >
                                        {val}
                                      </Box>
                                    ))}
                                </Paper>
                              )}
                          </Box>

                          {/* Remove */}
                          <IconButton
                            size="small"
                            onClick={() =>
                              filterClauses.length > 1 &&
                              setFilterClauses(
                                filterClauses.filter((c) => c.id !== clause.id),
                              )
                            }
                            disabled={filterClauses.length === 1}
                            sx={{ color: "red", p: 0.3, flexShrink: 0 }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ))}

                      <Button
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                        onClick={() =>
                          setFilterClauses([
                            ...filterClauses,
                            {
                              id: Date.now(),
                              field: "",
                              operator: "is equal to",
                              value: "",
                              conjunction: "And",
                            },
                          ])
                        }
                        sx={{
                          textTransform: "none",
                          fontSize: 11,
                          color: "#1976d2",
                          mt: 0.5,
                          px: 0,
                        }}
                      >
                        Add Clause
                      </Button>
                    </Box>
                  </Box>

                  {/* Invert Where Clause filter */}

                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Checkbox
                      size="small"
                      checked={filterInvertWhere}
                      onChange={(e) => setFilterInvertWhere(e.target.checked)}
                      sx={{ p: 0.5 }}
                    />
                    <Typography variant="body2" sx={{ fontSize: 12 }}>
                      Invert Where Clause
                    </Typography>
                  </Box>

                  {/* Active Filter Result - Invert Where ke neeche */}

                  {Object.keys(activeFilters).length > 0 && (
                    <Box
                      sx={{
                        mb: 1,
                        border: "1px solid #4caf50",
                        borderRadius: 1,
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      {/* Header */}
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.8,
                          bgcolor: "#e8f5e9",
                          borderBottom: "1px solid #4caf50",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: "#2e7d32",
                            fontSize: 11,
                          }}
                        >
                          ✅ Active Filters ({Object.keys(activeFilters).length}
                          )
                        </Typography>
                      </Box>

                      {/* Layer wise list */}
                      <Box
                        sx={{
                          maxHeight: 120,
                          overflowY: "auto",
                          bgcolor: "#f9fbe7",
                        }}
                      >
                        {Object.entries(activeFilters).map(
                          ([lKey, filter], layerIdx) => {
                            const layerName =
                              Object.values(layerGroups)
                                .flatMap((g) => Object.entries(g.children))
                                .find(([k]) => k === lKey)?.[1]?.name || lKey;

                            return (
                              <Box
                                key={lKey}
                                sx={{
                                  borderBottom:
                                    layerIdx <
                                    Object.keys(activeFilters).length - 1
                                      ? "1px solid #c8e6c9"
                                      : "none",
                                  bgcolor:
                                    filterLayer === lKey
                                      ? "#f1f8e9"
                                      : "transparent",
                                }}
                              >
                                {/* Layer Name */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    px: 1.5,
                                    py: 0.5,
                                    bgcolor: "#e8f5e9",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <LayersIcon
                                      sx={{ fontSize: 12, color: "#2e7d32" }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: "#1b5e20",
                                      }}
                                    >
                                      {layerName}
                                    </Typography>
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const olLayer =
                                        wmsLayersRef.current[lKey];
                                      if (olLayer) {
                                        olLayer.getSource().updateParams({
                                          CQL_FILTER: undefined,
                                        });
                                        olLayer.getSource().refresh();
                                      }
                                      setActiveFilters((prev) => {
                                        const u = { ...prev };
                                        delete u[lKey];
                                        return u;
                                      });
                                      const wmsLayerName =
                                        layerExtentsRef.current[lKey];
                                      if (wmsLayerName) {
                                        fetch(
                                          `${GEOSERVER_CONFIG.wfsUrl}?${new URLSearchParams({ ...DEFAULT_WFS_PARAMS, typeName: wmsLayerName })}`,
                                        )
                                          .then((r) => r.json())
                                          .then((data) => {
                                            if (data.features?.length > 0) {
                                              attributeFeaturesRef.current =
                                                data.features;
                                              setAttributeColumns(
                                                Object.keys(
                                                  data.features[0].properties,
                                                ).map((key) => ({
                                                  id: key,
                                                  label: key
                                                    .toUpperCase()
                                                    .replace(/_/g, " "),
                                                })),
                                              );
                                              setAttributeData(
                                                data.features.map((f, i) => ({
                                                  ...f.properties,
                                                  __rowId: i,
                                                })),
                                              );
                                              setAttributeLayerName(
                                                wmsLayerName,
                                              );
                                              setSelectedRows([]);
                                            }
                                          });
                                      }
                                    }}
                                    sx={{ p: 0.2 }}
                                    title="Remove filter"
                                  >
                                    <CloseIcon
                                      sx={{ fontSize: 13, color: "#f44336" }}
                                    />
                                  </IconButton>
                                </Box>

                                {/* Filter Expression - Scrollable */}
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 0.6,
                                    maxHeight: 80,
                                    overflowY: "auto",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: 10,
                                      color: "#1b5e20",
                                      fontFamily: "monospace",
                                      wordBreak: "break-all",
                                      display: "block",
                                      bgcolor: "white",
                                      p: 0.6,
                                      borderRadius: 0.5,
                                      border: "1px solid #c8e6c9",
                                    }}
                                  >
                                    {filter}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          },
                        )}
                      </Box>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Buttons */}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.8,
                      flexShrink: 0,
                      mb: 2,
                      mt: "auto",
                    }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      startIcon={<FilterIcon />}
                      onClick={async () => {
                        if (!filterLayer) {
                          alert("Please select a layer");
                          return;
                        }
                        const conditions = filterClauses
                          .filter(
                            (c) =>
                              c.field &&
                              (c.operator === "is blank" ||
                                c.operator === "is not blank" ||
                                c.value !== ""),
                          )
                          .map((c, idx) => {
                            let condition = "";
                            switch (c.operator) {
                              case "is equal to":
                                condition = `${c.field} = '${c.value}'`;
                                break;
                              case "is not equal to":
                                condition = `${c.field} <> '${c.value}'`;
                                break;
                              case "is greater than":
                                condition = `${c.field} > ${c.value}`;
                                break;
                              case "is less than":
                                condition = `${c.field} < ${c.value}`;
                                break;
                              case "contains":
                                condition = `${c.field} LIKE '%${c.value}%'`;
                                break;
                              case "starts with":
                                condition = `${c.field} LIKE '${c.value}%'`;
                                break;
                              case "is blank":
                                condition = `${c.field} IS NULL`;
                                break;
                              case "is not blank":
                                condition = `${c.field} IS NOT NULL`;
                                break;
                              default:
                                condition = `${c.field} = '${c.value}'`;
                            }
                            if (idx > 0) {
                              return (
                                (c.conjunction === "Or" ? " OR " : " AND ") +
                                condition
                              );
                            }
                            return condition;
                          });
                        if (conditions.length === 0) {
                          alert("Please fill at least one condition");
                          return;
                        }
                        let cqlFilter = conditions.join("");
                        if (filterInvertWhere) cqlFilter = `NOT (${cqlFilter})`;
                        applyFilterToLayer(filterLayer, cqlFilter);
                        setActiveFilters((prev) => ({
                          ...prev,
                          [filterLayer]: cqlFilter,
                        }));
                        try {
                          const wmsLayerName =
                            layerExtentsRef.current[filterLayer];
                          const filteredParams = new URLSearchParams({
                            ...DEFAULT_WFS_PARAMS,
                            typeName: wmsLayerName,
                            CQL_FILTER: cqlFilter,
                          });
                          const filteredData = await (
                            await fetch(
                              `${GEOSERVER_CONFIG.wfsUrl}?${filteredParams}`,
                            )
                          ).json();
                          if (filteredData.features?.length > 0) {
                            const cols = Object.keys(
                              filteredData.features[0].properties,
                            ).map((key) => ({
                              id: key,
                              label: key.toUpperCase().replace(/_/g, " "),
                            }));
                            attributeFeaturesRef.current =
                              filteredData.features;
                            setAttributeColumns(cols);
                            setAttributeData(
                              filteredData.features.map((f, i) => ({
                                ...f.properties,
                                __rowId: i,
                              })),
                            );
                            setAttributeLayerName(
                              `${wmsLayerName} (Filtered: ${filteredData.features.length})`,
                            );
                            setSelectedRows([]);
                          } else {
                            setAttributeData([]);
                          }
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                      sx={{
                        bgcolor: "#1976d2",
                        "&:hover": { bgcolor: "#115293" },
                      }}
                    >
                      Apply Filter
                    </Button>

                    <Box sx={{ display: "flex", gap: 0.8 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          const layerToReload = filterLayer;
                          const wmsLayerNameToReload =
                            layerExtentsRef.current[layerToReload];
                          const olLayerToReset = layerToReload
                            ? wmsLayersRef.current[layerToReload]
                            : null;
                          if (
                            layerToReload &&
                            activeFilters[layerToReload] &&
                            olLayerToReset
                          ) {
                            olLayerToReset.getSource().updateParams({
                              CQL_FILTER: null,
                              LAYERS: wmsLayerNameToReload,
                            });
                            olLayerToReset.getSource().refresh();
                          }
                          setActiveFilters((prev) => {
                            const updated = { ...prev };
                            delete updated[layerToReload];
                            return updated;
                          });
                          setFilterLayer("");
                          setFilterClauses([
                            {
                              id: Date.now(),
                              field: "",
                              operator: "is equal to",
                              value: "",
                            },
                          ]);
                          setFilterInvertWhere(false);
                          setFilterLayerFields([]);
                          if (wmsLayerNameToReload) {
                            try {
                              const res = await fetch(
                                `${GEOSERVER_CONFIG.wfsUrl}?${new URLSearchParams({ ...DEFAULT_WFS_PARAMS, typeName: wmsLayerNameToReload })}`,
                              );
                              const data = await res.json();
                              if (data.features?.length > 0) {
                                const cols = Object.keys(
                                  data.features[0].properties,
                                ).map((key) => ({
                                  id: key,
                                  label: key.toUpperCase().replace(/_/g, " "),
                                }));
                                const rows = data.features.map((f, i) => ({
                                  ...f.properties,
                                  __rowId: i,
                                }));
                                attributeFeaturesRef.current = data.features;
                                setAttributeColumns(cols);
                                setAttributeData(rows);
                                setAttributeLayerName(wmsLayerNameToReload);
                                setSelectedRows([]);
                                setAttributeTableOpen(true);
                                setAttributeTableDocked(true);
                              }
                            } catch (e) {
                              console.error("Reload error:", e);
                            }
                          }
                        }}
                        sx={{ borderColor: "#ff9800", color: "#ff9800" }}
                      >
                        Clear
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          Object.keys(activeFilters).forEach((lKey) => {
                            const olLayer = wmsLayersRef.current[lKey];
                            if (olLayer) {
                              olLayer
                                .getSource()
                                .updateParams({ CQL_FILTER: undefined });
                              olLayer.getSource().refresh();
                            }
                          });
                          setActiveFilters({});
                          setFilterLayer("");
                          setFilterClauses([
                            {
                              id: Date.now(),
                              field: "",
                              operator: "is equal to",
                              value: "",
                            },
                          ]);
                          setFilterInvertWhere(false);
                          setFilterLayerFields([]);
                        }}
                        disabled={Object.keys(activeFilters).length === 0}
                        sx={{
                          borderColor: "#f44336",
                          color: "#f44336",
                          "&:hover": {
                            bgcolor: "#ffebee",
                            borderColor: "#f44336",
                          },
                          "&.Mui-disabled": { opacity: 0.4 },
                          fontSize: 11,
                        }}
                      >
                        Clear All ({Object.keys(activeFilters).length})
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={() => onCloseTool("filter")}
                        sx={{
                          borderColor: "#f44336",
                          color: "#f44336",
                          "&:hover": {
                            bgcolor: "#ffebee",
                            borderColor: "#f44336",
                          },
                        }}
                      >
                        Close
                      </Button>
                    </Box>
                  </Box>
                </Box>
  );
}
